// The Lobby's « Table » tab (owner's request: a tab of its own for the table's colour and the draw pile's
// side): a live picture of the player's table as a game shows it — their felt, the draw pile and the
// discard pile on their side, groups on the table, a hand —, then the two choices. Both are kept on the
// device: the colour by lib/tableThemes.js (locks, videos), the side by lib/preferences.js (`pioche_side`,
// read by the game page and the tutorial), which a player could only set as a guest's second step before.
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Columns2 } from "lucide-react";
import { PlayingCard } from "../PlayingCard";
import { ColorWell, TableColorGrid } from "../TableColorPicker";
import { getPiocheSide, setPiocheSide } from "../../lib/preferences";
import { matStyle, useMyTableTheme } from "../../lib/tableThemes";
import { t } from "../../lib/i18n";

const GROUPS = [
  [{ id: "g1", rank: "5", suit: "spades" }, { id: "g2", rank: "6", suit: "spades" }, { id: "g3", rank: "7", suit: "spades" }],
  [{ id: "g4", rank: "K", suit: "hearts" }, { id: "g5", rank: "K", suit: "clubs" }, { id: "g6", rank: "K", suit: "diamonds" }],
];
// Five cards: the hand stays clear of the draw pile's column on either side.
const HAND = [
  { id: "h1", rank: "9", suit: "clubs" }, { id: "h2", rank: "10", suit: "diamonds" }, { id: "h3", rank: "J", suit: "spades" },
  { id: "h4", rank: "Q", suit: "hearts" }, { id: "h5", rank: "JOKER" },
];

// The game board never mirrors (dir="ltr"), like Game.jsx.
function TablePreview({ themeId, side }) {
  const left = side === "left";
  return (
    <div
      dir="ltr"
      aria-hidden="true"
      className="pointer-events-none relative h-40 overflow-hidden rounded-2xl border border-amber-400/25 shadow-[inset_0_0_30px_rgba(0,0,0,0.5)]"
      data-testid="table-preview"
    >
      <motion.div key={themeId} className="rami-mat absolute inset-0" style={matStyle(themeId)} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} />
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className={`absolute top-2.5 flex flex-col items-center gap-1.5 ${left ? "left-3" : "right-3"}`}
      >
        <span className="text-[8px] uppercase tracking-[0.18em] text-amber-200/80">{t("pioche.pile")}</span>
        <PlayingCard faceDown size="sm" />
        <PlayingCard card={{ id: "d1", rank: "7", suit: "hearts" }} size="sm" />
      </motion.div>
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className={`absolute top-3 flex flex-wrap gap-1.5 ${left ? "left-[4.5rem] right-3" : "left-3 right-[4.5rem]"}`}
      >
        {GROUPS.map((g, i) => (
          <div key={i} className="flex w-fit gap-0.5 rounded-lg border border-white/10 bg-black/25 p-1">
            {g.map((c) => <PlayingCard key={c.id} card={c} size="xs" />)}
          </div>
        ))}
      </motion.div>
      <div className="absolute -bottom-6 left-1/2 flex -translate-x-1/2 -space-x-2.5">
        {HAND.map((c) => <PlayingCard key={c.id} card={c} size="sm" />)}
      </div>
    </div>
  );
}

function Section({ lead, title, children, testId }) {
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-slate-900/80 p-3 shadow-[0_6px_24px_rgba(0,0,0,0.3)]" data-testid={testId}>
      <h3 className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-amber-300/90">
        {lead}
        {title}
      </h3>
      {children}
    </section>
  );
}

export function TableTab({ onSeen }) {
  const themeId = useMyTableTheme();
  const [side, setSide] = useState(getPiocheSide);
  // Opening the tab shows the table colours: their « new » dot goes.
  useEffect(() => { onSeen?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (s) => {
    setSide(s);
    try { setPiocheSide(s); } catch { /* private mode: kept for this visit */ }
  };

  return (
    <div className="mx-auto max-w-xl space-y-2" data-testid="table-tab">
      <TablePreview themeId={themeId} side={side} />
      <p className="px-1 text-center text-[11px] text-slate-400">{t("lobby.hub.tablePreview")}</p>

      <Section lead={<Columns2 className="h-3.5 w-3.5" />} title={t("common.piocheSide")} testId="table-pioche">
        {/* Always left / right as on screen, whatever the language. */}
        <div dir="ltr" role="radiogroup" aria-label={t("common.piocheSide")} className="grid grid-cols-2 gap-1 rounded-full bg-slate-950/70 p-1">
          {["left", "right"].map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={side === s}
              onClick={() => pick(s)}
              data-testid={`pioche-side-${s}`}
              className={`rounded-full py-1.5 text-xs transition-colors ${side === s ? "bg-amber-400 text-slate-950" : "text-slate-400 hover:text-white"}`}
            >
              {s === "left" ? `← ${t("common.left")}` : `${t("common.right")} →`}
            </button>
          ))}
        </div>
      </Section>

      <Section lead={<ColorWell id={themeId} className="h-4 w-4" />} title={t("tableColor.title")} testId="table-colors">
        <TableColorGrid />
      </Section>
    </div>
  );
}

export default TableTab;
